import type { CanonicalQuestion, QuestionByType } from '../model/canonical.ts';
import { isAnswered, type Outcome, type QuestionScore, type Response, type ResponseByType } from '../model/session.ts';
import type { QuestionType } from '../schema/question-types.ts';
import { matchesAccepted } from './textNormalize.ts';

/**
 * One scoring strategy per question type.
 *
 * Pure functions of a question and a response: no session policy, no clock, no translation. Policy
 * decides *when* a result is shown, never what it is worth — keeping that separation is what stops
 * "practice mode scores differently" from ever becoming true by accident.
 *
 * The registry is a mapped type over the question union, so adding a question type breaks the build
 * here until it has a strategy. That is the guard rail that replaces a `switch` scattered across
 * the app.
 */
export interface ScoringStrategy<T extends QuestionType> {
  readonly type: T;
  score(question: QuestionByType[T], response: ResponseByType[T] | null): QuestionScore;
}

export type ScoringRegistry = { [T in QuestionType]: ScoringStrategy<T> };

const unanswered = (max: number): QuestionScore => ({ raw: 0, max, outcome: 'unanswered' });

/**
 * All-or-nothing by default, with the shape partial credit will need.
 *
 * `raw`/`max` rather than a boolean from the very first version: once partial credit exists for
 * matching and ordering, nothing about the recorded data or the percentage has to change.
 */
const graded = (correct: number, total: number, max: number, mode: 'all-or-nothing' | 'partial'): QuestionScore => {
  if (correct === total) {
    return { raw: max, max, outcome: 'correct' };
  }
  if (mode === 'partial' && correct > 0) {
    return { raw: (max * correct) / total, max, outcome: 'partial' };
  }
  return { raw: 0, max, outcome: 'incorrect' };
};

export const SCORING: ScoringRegistry = {
  'single-choice': {
    type: 'single-choice',
    score(question, response) {
      const max = question.scoring.points;
      if (response === null || !isAnswered(response)) {
        return unanswered(max);
      }
      const correct = response.optionId === question.interaction.correctOptionId;
      return { raw: correct ? max : 0, max, outcome: correct ? 'correct' : 'incorrect' };
    },
  },

  'multiple-choice': {
    type: 'multiple-choice',
    score(question, response) {
      const max = question.scoring.points;
      if (response === null || !isAnswered(response)) {
        return unanswered(max);
      }

      const chosen = new Set(response.optionIds);
      const key = new Set(question.interaction.correctOptionIds);

      // A wrong option selected is not merely a missing right one: a candidate who ticks everything
      // must not score the same as one who ticked exactly the right two.
      const hits = [...key].filter((id) => chosen.has(id)).length;
      const falsePositives = [...chosen].filter((id) => !key.has(id)).length;

      if (falsePositives > 0) {
        return question.scoring.mode === 'partial'
          ? graded(Math.max(0, hits - falsePositives), key.size, max, 'partial')
          : { raw: 0, max, outcome: 'incorrect' };
      }

      return graded(hits, key.size, max, question.scoring.mode);
    },
  },

  matching: {
    type: 'matching',
    score(question, response) {
      const max = question.scoring.points;
      if (response === null || !isAnswered(response)) {
        return unanswered(max);
      }

      const given = new Map(response.pairs.map((pair) => [pair.itemId, pair.targetId]));
      const correct = question.interaction.pairs.filter(
        (pair) => given.get(pair.itemId) === pair.targetId,
      ).length;

      return graded(correct, question.interaction.pairs.length, max, question.scoring.mode);
    },
  },

  ordering: {
    type: 'ordering',
    score(question, response) {
      const max = question.scoring.points;
      if (response === null || !isAnswered(response)) {
        return unanswered(max);
      }

      const expected = question.interaction.correctOrder;
      const given = response.order;

      // Position-by-position, so partial credit means "how much of the sequence is in the right
      // place" rather than "how many of the items are present".
      const correct = expected.filter((id, position) => given[position] === id).length;
      return graded(correct, expected.length, max, question.scoring.mode);
    },
  },

  text: {
    type: 'text',
    score(question, response) {
      const max = question.scoring.points;

      // Self-evaluated questions have no answer key by definition. Scoring them as wrong would
      // punish candidates for answering the open questions, so they stay out of the percentage
      // until the candidate resolves them during review.
      if (question.interaction.evaluation === 'self') {
        return { raw: 0, max, outcome: 'ungraded' };
      }
      if (response === null || !isAnswered(response)) {
        return unanswered(max);
      }

      const correct = matchesAccepted(
        response.text,
        question.interaction.accepted,
        question.interaction.normalization,
      );
      return { raw: correct ? max : 0, max, outcome: correct ? 'correct' : 'incorrect' };
    },
  },
};

/**
 * Scores any question with the strategy for its type.
 *
 * A response of the wrong shape for the question is treated as no answer rather than as an error:
 * it can only come from a hand-edited session file, and refusing to open the file would help
 * nobody.
 */
export function scoreQuestion(
  question: CanonicalQuestion,
  response: Response | null,
): QuestionScore {
  const usable = response !== null && response.type === question.type ? response : null;

  switch (question.type) {
    case 'single-choice':
      return SCORING['single-choice'].score(question, usable as ResponseByType['single-choice'] | null);
    case 'multiple-choice':
      return SCORING['multiple-choice'].score(question, usable as ResponseByType['multiple-choice'] | null);
    case 'matching':
      return SCORING.matching.score(question, usable as ResponseByType['matching'] | null);
    case 'ordering':
      return SCORING.ordering.score(question, usable as ResponseByType['ordering'] | null);
    case 'text':
      return SCORING.text.score(question, usable as ResponseByType['text'] | null);
  }
}

/** Applies a candidate's own verdict to a question the app could not score. */
export function applySelfGrade(score: QuestionScore, correct: boolean): QuestionScore {
  if (score.outcome !== 'ungraded') {
    return score;
  }
  const outcome: Outcome = correct ? 'correct' : 'incorrect';
  return { raw: correct ? score.max : 0, max: score.max, outcome };
}
